import React, { useEffect, useState, useRef } from 'react';
import { useAtom } from 'jotai';
import { Tooltip, Button, Modal, Switch, Tag, Alert, Descriptions, Input, message, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { mapState, allScanDataState, currentMapIDState } from '../stateManagement/commonState';
import dayjs from 'dayjs';
import { getMassSpecDataById, fetchSpectrum, addSpectrumToValidatedSpectra, fetchReferenceSubstanceNames } from '../utils/dbHelpers.js';

const { TextArea } = Input;

const SpectrumValidation = ({ linechartSpectrum, expectedClassifications, combinedSpectrumR2, r2Contributions, maxReferenceIntensities }) => {
  const [currentMapID] = useAtom(currentMapIDState);
  const [allScanData] = useAtom(allScanDataState);
  const [isReference, setIsReference] = useState(false);
  const [map] = useAtom(mapState);
  const [open, setOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const mapRef = useRef();
  const isReferenceRef = useRef();
  const mapIDRef = useRef(null);
  const currentScanDataRef = useRef();
  const [errorMessage, setErrorMessage] = useState(null);
  const [currentScanData, setCurrentScanData] = useState(null);
  const [labSampleData, setLabSampleData] = useState(null);
  const [warningMessage, setWarningMessage] = useState(null);
  const [validationPassword, setValidationPassword] = useState('');
  const [notes, setNotes] = useState('');
  const [editableExpectedClassifications, setEditableExpectedClassifications] = useState([]);
  const [classificationOptions, setClassificationOptions] = useState([]);

  useEffect(() => {
    if (open) {
      mapRef.current = map;
      isReferenceRef.current = isReference;
      mapIDRef.current = currentMapID;
      currentScanDataRef.current = currentScanData;
    }
  }, [open, map, isReference, currentMapID, currentScanData]);

  useEffect(() => {
    if (open) {
      setCurrentScanData(allScanData.find(scan => scan.map_id === currentMapID));
    }
  }, [open, allScanData, currentMapID]);

  useEffect(() => {
    if (open && currentScanData) {
      fetchRecordLabSampleData(currentScanData?.lab_sample_id);
    }
  }, [currentScanData]);

  // Prefill from chart references when the modal opens; user can then add/remove freely.
  useEffect(() => {
    if (open) {
      setEditableExpectedClassifications([...(expectedClassifications || [])]);
    }
  }, [open, expectedClassifications]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadOptions = async () => {
      try {
        const names = await fetchReferenceSubstanceNames();
        if (cancelled || !names) return;
        setClassificationOptions(
          names
            .map(e => e.substance_description)
            .filter(Boolean)
            .map(name => ({ value: name, label: name }))
        );
      } catch {
        // Options are optional; free-form tags still work without them.
      }
    };
    loadOptions();
    return () => { cancelled = true; };
  }, [open]);

  const showModal = () => {
    const scanData = allScanData.find(scan => scan.map_id === currentMapID);
    const scanNotes = scanData?.notes;
    if (scanNotes) {
      navigator.clipboard.writeText(scanNotes).catch(() => {
        // Clipboard may be unavailable (permissions / insecure context); ignore.
      });
    }
    setOpen(true);
  };

  const fetchRecordLabSampleData = async id => {
    if (!id) {
      setWarningMessage('No lab sample ID found.');
      return;
    }
    try {
      const labSampleData = await getMassSpecDataById(id);
      setLabSampleData(labSampleData);
    } catch (error) {
      setWarningMessage('Failed to fetch lab sample data.');
    }
    return;
  }

  const resetAndCloseModal = () => {
    setCurrentScanData(null);
    setOpen(false);
    setConfirmLoading(false);
    setIsReference(false);
    setErrorMessage(null);
    setWarningMessage(null);
    setValidationPassword('');
    setLabSampleData(null);
    setNotes('');
    setEditableExpectedClassifications([]);
  };

  const handleOk = async () => {
    setConfirmLoading(true);
    try {

      if (validationPassword !== '654') { throw new Error('Invalid password.'); }

      const normalizedClassifications = editableExpectedClassifications
        .map(name => (typeof name === 'string' ? name.trim() : name))
        .filter(Boolean);

      // R² / intensity checks only apply when reference spectra were added to the chart.
      // Reference spectra can be validated without chart overlays.
      const hasChartReferences = (expectedClassifications?.length ?? 0) > 0;
      if (hasChartReferences) {
        if (Object.values(r2Contributions || {}).some(value => value <= 0)) { throw new Error('R² contributions must be greater than 0.'); }
        if (Object.values(maxReferenceIntensities || {}).some(value => value <= 60)) { throw new Error('Max reference intensities must be greater than 60.'); }
      }

      const spectrumFromDB = (await fetchSpectrum(mapIDRef.current, linechartSpectrum.index))?.[0] ?? null;

      if (!spectrumFromDB) { throw new Error('Spectrum not found in database.'); }
      if (spectrumFromDB.index !== linechartSpectrum.index) { throw new Error('Chart and database index mismatch detected.'); }

      const areEqual =
        linechartSpectrum.processedSpectrum.length === spectrumFromDB.processed_spectrum.length &&
        linechartSpectrum.processedSpectrum.every((val, idx) => val === spectrumFromDB.processed_spectrum[idx]);

      if (!areEqual) { throw new Error('Spectrum from chart does not match database.'); }

      const successfullyAdded = await addSpectrumToValidatedSpectra({
        serial: currentScanDataRef.current?.serial,
        ...spectrumFromDB,
        expected_classifications: normalizedClassifications,
        reference: isReferenceRef.current,
        notes: notes,
        r2: hasChartReferences ? combinedSpectrumR2 : null,
        r2_contributions: hasChartReferences ? r2Contributions : null,
        max_reference_intensities: hasChartReferences ? maxReferenceIntensities : null
      });

      if (!successfullyAdded) { throw new Error('Failed to add spectrum to validated spectra.'); }

      message.success('Spectrum successfully validated.');

      resetAndCloseModal();

    } catch (error) {
      setErrorMessage(error.message);
      setConfirmLoading(false);
    }

  };
  const handleCancel = () => {
    resetAndCloseModal();
  };

  return (
    <div style={{ marginLeft: '3px' }}>
      <Tooltip placement="topRight" title={`Add to validated spectra`}>
        <Button icon={<PlusOutlined />} disabled={linechartSpectrum?.index == null} onClick={() => { showModal() }}></Button>
      </Tooltip>
      <Modal
        title="Validate Spectrum"
        open={open}
        onOk={handleOk}
        confirmLoading={confirmLoading}
        onCancel={handleCancel}

      >
        <Descriptions style={{ margin: '12px 0' }} title="" bordered={true} column={1} size="small">
          <Descriptions.Item label="Serial" labelStyle={{ width: '40px' }}><div>{currentScanData?.serial}</div></Descriptions.Item>
          <Descriptions.Item label="Index">{linechartSpectrum?.index}</Descriptions.Item>
          <Descriptions.Item label="Date">{currentScanData?.created_at ? dayjs(currentScanData?.created_at).format('MMM D, YYYY') : null}</Descriptions.Item>
          <Descriptions.Item label="Description">{currentScanData?.substance_description ? currentScanData?.substance_description : ''}</Descriptions.Item>
          <Descriptions.Item label="Scan Notes"> {currentScanData?.notes ? currentScanData?.notes : ''}</Descriptions.Item>
          <Descriptions.Item label="Laser Power">{linechartSpectrum?.laserPower ? `${linechartSpectrum?.laserPower}mW` : null}</Descriptions.Item>
          <Descriptions.Item label="Int. Time">{linechartSpectrum?.integrationTime ? `${linechartSpectrum?.integrationTime}ms` : null}</Descriptions.Item>
          <Descriptions.Item label="Lab Results">{
            Object.entries(labSampleData?.results || {}).map(([substance, quantification], index) => (
              <Tag key={index} color="blue"><b>{substance}</b>: {quantification}</Tag>
            ))
          }</Descriptions.Item>
          <Descriptions.Item label="Expected Classifications">
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Type a name and press Enter, or select from the list"
              value={editableExpectedClassifications}
              onChange={setEditableExpectedClassifications}
              options={classificationOptions}
              allowClear
            />
          </Descriptions.Item>
          {expectedClassifications?.length ? (
            <>
              <Descriptions.Item label="R²">{combinedSpectrumR2?.toFixed(3)}</Descriptions.Item>
              <Descriptions.Item label="ΔR² Values">
                {Object.entries(r2Contributions || {}).map(([substance, r2Contribution], index) => (
                  <Tag key={index} color="blue"><b>{substance}</b>: {r2Contribution?.toFixed(3)}</Tag>
                ))}
              </Descriptions.Item>
              <Descriptions.Item label="Max Intensity">
                {Object.entries(maxReferenceIntensities || {}).map(([substance, maxIntensity], index) => (
                  <Tag key={index} color="blue"><b>{substance}</b>: {maxIntensity?.toFixed(0)}</Tag>
                ))}
              </Descriptions.Item>
            </>
          ) : null}
          <Descriptions.Item label="Reference"><Switch size="small" checkedChildren={'Yes'} unCheckedChildren={'No'} value={isReference} onChange={e => { setIsReference(e); }} /></Descriptions.Item>
          <Descriptions.Item label="Validation Notes"><TextArea rows={2} value={notes} onChange={e => { setNotes(e.target.value) }} /></Descriptions.Item>
          <Descriptions.Item label="Password"><Input variant="borderless" value={validationPassword} onChange={e => { setValidationPassword(e.target.value) }} /></Descriptions.Item>
        </Descriptions>
        {warningMessage ? <Alert message={warningMessage} type="warning" showIcon /> : null}
        {errorMessage ? <Alert message={errorMessage} type="error" showIcon /> : null}
      </Modal>
    </div>
  );
};
export default SpectrumValidation;
